import React, {
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
} from "react";
import Select from "react-select";
import Table from "../../../ui/Table";
import Modal from "../../../ui/Modal";
import Loader from "../../../ui/Loader";
import ModalTableInput from "../../../ui/ModalTableInput";
import { AuthContext } from "../../../../context/AuthContext";
import {
  swalConfirm,
  swalError,
  swalSuccess,
  swalWarning,
} from "../../../../utils/swalAlert";
import { fetchWithAuth } from "../../../../utils/api";
import ManageEnrollments from "../../Instructor/pageUI/ManageEnrollments";
import ManageTests from "./ManageTests";

const ManageClasses = React.forwardRef((props, ref) => {
  const { user } = React.useContext(AuthContext);
  const [institute, setInstitute] = useState(null);
  const [classes, setClasses] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [courses, setCourses] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [levels, setLevels] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEnrollmentsModalOpen, setIsEnrollmentsModalOpen] = useState(false);
  const [isTestsModalOpen, setIsTestsModalOpen] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [selectedClassForTests, setSelectedClassForTests] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [newClass, setNewClass] = useState({
    instructorId: "",
    courseId: "",
    periodId: "",
    enrollmentEndDate: "",

    name: "",

    level: null,
  });

  const [editClass, setEditClass] = useState(null);
  const [initialEditClass, setInitialEditClass] = useState(null);
  const [isEditChanged, setIsEditChanged] = useState(false);
  const [classTests, setClassTests] = useState([]); // New state for class tests
  const [isLoadingTests, setIsLoadingTests] = useState(false); // New state for loading tests
  const [hasLevels, setHasLevels] = useState(false);
  const [isIndirectAttainment, setIsIndirectAttainment] = useState(false);
  const [courseHasProgram, setCourseHasProgram] = useState({});
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [initialPeriodDetermined, setInitialPeriodDetermined] = useState(false);

  const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

  const isLanguageInst = institute?.category === "LANGUAGE_LEARNING_INSTITUTE";

  const columns = [
    { key: "periodName", label: isLanguageInst ? "Term / Period" : "Period", sortable: true, filter: true },
    { key: "level", label: isLanguageInst ? "CEFR Level" : "Level", filter: true },
    { key: "name", label: isLanguageInst ? "Batch Name / Number" : "Class Name", sortable: true, filter: true },
    {
      key: "instructorName",
      label: isLanguageInst ? "Assigned Teacher" : "Instructor",
      sortable: true,
      filter: true,
    },

    { key: "tests", label: isLanguageInst ? "Exams & Quizzes" : "Tests", sortable: false },
    { key: "enrollments", label: "Enrollments", sortable: false },
    { key: "edit", label: "", sortable: false },
    { key: "delete", label: "", sortable: false },
  ];

  const convertToUserTimezone = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const tzOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() + tzOffset).toISOString();
  };

  const userDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const tzOffset = date.getTimezoneOffset() * 60000;
    if (tzOffset > 0) {
      return new Date(date.getTime() + tzOffset).toISOString().split("T")[0];
    } else {
      return new Date(date.getTime() - tzOffset).toISOString().split("T")[0];
    }
  };

  // Fetch institute data
  useEffect(() => {
    const fetchInstituteData = async () => {
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/institute/get/${user.instituteId}`,
        );
        if (!response) return;
        if (!response.ok) throw new Error("Failed to fetch institute data");
        const data = await response.json();
        setInstitute(data);

        if (data.levels && data.levels.length > 0) {
          setHasLevels(true);
          setLevels(
            data.levels.map((level) => ({
              value: level,
              label: level,
            })),
          );
        } else {
          setHasLevels(false);
        }
        if (data.subjects && data.subjects.length > 0) {
          setSubjects(
            data.subjects.map((subject) => ({
              value: subject,
              label: subject,
            })),
          );
        }
      } catch (err) {
        swalError(err.message || "Failed to fetch institute data.");
      }
    };
    fetchInstituteData();
  }, [user.instituteId, serverUrl, hasLevels]);

  const fetchClasses = useCallback(async () => {
    try {
      setLoading(true);
      const periodQuery = selectedPeriod?.value && selectedPeriod.value !== 'all' ? `?periodId=${selectedPeriod.value}` : "";
      const response = await fetchWithAuth(
        `${serverUrl}/api/class/get-all/institute/${user.instituteId}${periodQuery}`,
        {
          credentials: "include",
        },
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch classes");
      const data = await response.json();

      // For Practice Zone classes, fetch enrolled student details so we can show (student.firstName + " " + student.lastName)
      const classesWithPracticeStudents = await Promise.all(
        data.map(async (cls) => {
          const isPZ = cls.isPracticeZone === true || cls.name === "Practice Zone";
          if (isPZ && !cls.student) {
            try {
              const enrollRes = await fetchWithAuth(
                `${serverUrl}/api/enrollment/get-all/${cls._id || cls.id}`,
                { credentials: "include" },
              );
              if (enrollRes && enrollRes.ok) {
                const students = await enrollRes.json();
                if (Array.isArray(students) && students.length > 0) {
                  return {
                    ...cls,
                    isPracticeZone: true,
                    student: students[0],
                  };
                }
              }
            } catch {
              // Ignore failure for individual enrollment fetch
            }
            return { ...cls, isPracticeZone: true };
          }
          return cls;
        }),
      );

      setClasses(classesWithPracticeStudents);
      setError(null);
    } catch (err) {
      setError(err.message);
      swalError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user.instituteId, serverUrl, selectedPeriod]);

  const fetchInstructors = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/user/get-all/${user.instituteId}/instructor`,
        {
          credentials: "include",
        },
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch instructors");
      const data = await response.json();
      setInstructors(data);
    } catch (err) {
      swalError(err.message);
    }
  }, [user.instituteId, serverUrl]);

  const fetchCourses = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/course/get-all/${user.instituteId}`,
        {
          credentials: "include",
        },
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch courses");
      const data = await response.json();
      setCourses(data);
      const programMap = {};
      data.forEach((course) => {
        programMap[course.id || course._id] = !!course.programId;
      });
      setCourseHasProgram(programMap);
    } catch (err) {
      swalError(err.message);
    }
  }, [user.instituteId, serverUrl]);

  const fetchPeriods = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/period/get-all/${user.instituteId}`,
        {
          credentials: "include",
        },
      );
      if (!response) {
        setInitialPeriodDetermined(true);
        return;
      }
      if (!response.ok) throw new Error("Failed to fetch periods");
      const data = await response.json();
      setPeriods(data);

      if (data.length > 0) {
        const ongoing = data.find((p) => p.status === "Ongoing");
        const selected = ongoing || data[0];
        const selectedId = selected.id || selected._id;

        setNewClass((prev) => ({
          ...prev,
          periodId: selectedId,
          enrollmentEndDate: selected.enrollmentEndDate
            ? userDate(selected.enrollmentEndDate)
            : "",
        }));

        setSelectedPeriod({
          value: selectedId,
          label: `${selected.term.charAt(0).toUpperCase() + selected.term.slice(1)} ${selected.year}`
        });
      }
      setInitialPeriodDetermined(true);
    } catch (err) {
      swalError(err.message);
      setInitialPeriodDetermined(true);
    }
  }, [user.instituteId, serverUrl]);

  const fetchLevels = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/lov/get-all-by-type/level`,
        {
          credentials: "include",
        },
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch levels");
      const data = await response.json();
      if (!hasLevels) {
        setLevels(
          data
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((level) => ({
              value: level.value,
              label: level.value,
            })),
        );
      }
    } catch (err) {
      swalError(err.message);
    }
  }, [serverUrl, hasLevels]);

  useEffect(() => {
    fetchInstructors();
    fetchCourses();
    fetchPeriods();
    fetchLevels();
  }, [fetchInstructors, fetchCourses, fetchPeriods, fetchLevels]);

  useEffect(() => {
    if (initialPeriodDetermined) {
      fetchClasses();
    }
  }, [initialPeriodDetermined, selectedPeriod?.value, fetchClasses]);

  const resetCreateForm = () => {
    const ongoing = periods.find((p) => p.status === "Ongoing");
    setNewClass({
      instructorId: "",
      courseId: "",
      periodId: ongoing ? ongoing.id || ongoing._id : "",
      enrollmentEndDate: ongoing ? userDate(ongoing.enrollmentEndDate) : "",
      name: "",
      level: null,
    });
  };

  const openModal = () => {
    resetCreateForm();
    setIsCreateModalOpen(true);
  };

  useImperativeHandle(ref, () => ({
    openModal,
  }));

  const handleSelectChange = (selectedOption, { name }) => {
    if (name === "instructorId") {
      setNewClass((prev) => ({
        ...prev,
        instructorId: selectedOption ? selectedOption.value : "",
      }));
    } else if (name === "periodId") {
      const selectedPeriod = periods.find(
        (p) => p.id === selectedOption.value || p._id === selectedOption.value,
      );
      const enrollmentEndDate = selectedPeriod
        ? userDate(selectedPeriod.enrollmentEndDate)
        : "";
      setNewClass((prev) => ({
        ...prev,
        periodId: selectedOption ? selectedOption.value : "",
        enrollmentEndDate,
      }));
    } else if (name === "level") {
      setNewClass((prev) => ({
        ...prev,
        level: selectedOption ? selectedOption.value : "",
      }));
    } else {
      setNewClass((prev) => ({
        ...prev,
        [name]: selectedOption ? selectedOption.value : "",
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewClass((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Helper function to compare indirectCOAttainments arrays
  const areIndirectCOAttainmentsEqual = (arr1, arr2) => {
    if (!arr1 && !arr2) return true;
    if (!arr1 || !arr2) return false;
    if (arr1.length !== arr2.length) return false;

    return arr1.every((item1, index) => {
      const item2 = arr2[index];
      return (
        item1.coLevel === item2.coLevel &&
        item1.attainment === item2.attainment &&
        item1.source === item2.source
      );
    });
  };

  // Handler for indirect CO attainment changes
  const handleIndirectCOChange = (coLevel, field, value, isEdit) => {
    const target = isEdit ? editClass : newClass;
    const attainments = [...(target.indirectCOAttainments || [])];

    // Find existing entry for this CO
    const existingIndex = attainments.findIndex(
      (att) => att.coLevel === coLevel,
    );

    if (existingIndex >= 0) {
      // Update existing entry
      attainments[existingIndex] = {
        ...attainments[existingIndex],
        [field]: value,
      };
    } else {
      // Create new entry
      attainments.push({
        coLevel: coLevel,
        attainment: field === "attainment" ? value : "",
        source: field === "source" ? value : "",
      });
    }

    if (isEdit) {
      setEditClass({ ...editClass, indirectCOAttainments: attainments });
    } else {
      setNewClass({ ...newClass, indirectCOAttainments: attainments });
    }
  };

  const renderIndirectCOAttainmentsTable = (isEdit = false) => {
    const classData = isEdit ? editClass : newClass;
    const isSubmitting = isEdit ? isUpdating : isCreating;

    const course = courses.find(
      (c) => c._id.toString() === classData.courseId.toString(),
    );
    const courseOutcomes = course?.outcomes || [];

    // Create rows based on course outcomes
    const rows = courseOutcomes.map((outcome) => {
      // Find existing attainment for this CO
      const existingAttainment = (classData.indirectCOAttainments || []).find(
        (att) => att.coLevel === outcome.code,
      );

      return {
        coLevel: outcome.code,
        attainment: existingAttainment?.attainment || "",
        source: existingAttainment?.source || "",
      };
    });

    const headers = [
      {
        key: "coLevel",
        label: "CO Level",
        width: "150px",
        required: true,
      },
      {
        key: "attainment",
        label: "Attainment",
        width: "150px",
        required: true,
        placeholder: "e.g., 0.75",
      },
      {
        key: "source",
        label: "Source",
        width: "1fr",
        required: false,
        placeholder: "e.g., Course Exit Survey",
      },
    ];

    const renderCell = (header, row, rowIndex) => {
      switch (header.key) {
        case "coLevel":
          return (
            <input
              type="text"
              value={row.coLevel}
              required
              disabled
              className="modal-table-input"
              style={{
                backgroundColor: "#f5f5f5",
                cursor: "not-allowed",
                color: "#666",
              }}
            />
          );
        case "attainment":
          return (
            <input
              type="number"
              value={row.attainment}
              onChange={(e) =>
                handleIndirectCOChange(
                  row.coLevel,
                  "attainment",
                  parseFloat(e.target.value) || "",
                  isEdit,
                )
              }
              required
              placeholder={header.placeholder}
              disabled={isSubmitting}
              min="0"
              max="3"
              step="0.25"
              className="modal-table-input"
            />
          );
        case "source":
          return (
            <input
              type="text"
              value={row.source}
              onChange={(e) =>
                handleIndirectCOChange(
                  row.coLevel,
                  "source",
                  e.target.value,
                  isEdit,
                )
              }
              placeholder={header.placeholder}
              disabled={isSubmitting}
              className="modal-table-input"
            />
          );
        default:
          return null;
      }
    };

    return (
      <div className="form-group" style={{ marginTop: "20px" }}>
        <label>Indirect CO Attainments</label>
        {courseOutcomes.length === 0 ? (
          <p style={{ color: "#666", fontStyle: "italic" }}>
            Please select a course first to view CO attainments
          </p>
        ) : (
          <ModalTableInput
            headers={headers}
            rows={rows}
            onRowChange={() => {}}
            onRowRemove={() => {}}
            onRowAdd={null} // No add button needed
            renderCell={renderCell}
            addButtonText="" // No add button
          />
        )}
      </div>
    );
  };

  const handleCreateClass = async (e) => {
    e.preventDefault();
    if (!newClass.instructorId) {
      swalError("Please select an Instructor.");
      return;
    }
    if (courses.length > 0 && !newClass.courseId) {
      swalError("Please select a Course.");
      return;
    }
    if (courses.length === 0 && !newClass.level) {
      swalError("Please select a Level.");
      return;
    }

    setIsCreating(true);
    try {
      let levelToSend = null;
      if (courses.length > 0 && newClass.courseId) {
        const selectedCourse = courses.find(
          (c) => c.id === newClass.courseId || c._id === newClass.courseId,
        );
        levelToSend = selectedCourse?.level;
      } else {
        levelToSend = newClass.level;
      }
      const { name, ...rest } = newClass;
      const classToCreate = {
        ...rest,
        enrollmentEndDate: newClass.enrollmentEndDate
          ? convertToUserTimezone(newClass.enrollmentEndDate)
          : "",
        className: name,

        level: levelToSend,
        periodId: newClass.periodId || null,
        instituteId: user.instituteId,
      };
      if (courses.length === 0) {
        delete classToCreate.courseId;
      }
      const response = await fetchWithAuth(`${serverUrl}/api/class/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(classToCreate),
        credentials: "include",
      });
      if (!response) return;
      if (!response.ok) {
        let errorText;
        try {
          errorText = await response.json();
        } catch {
          errorText = await response.text();
        }
        throw new Error(
          errorText.message || "Failed to create class. Please try again.",
        );
      }
      await fetchClasses();
      setIsCreateModalOpen(false);
      resetCreateForm();
      swalSuccess("Class created successfully!");
    } catch (error) {
      swalError("Failed to create class!");
    } finally {
      setIsCreating(false);
    }
  };

  const fetchClassTests = async (classId) => {
    try {
      setIsLoadingTests(true);
      const response = await fetchWithAuth(
        `${serverUrl}/api/test/get-all/${classId}`,
        {
          credentials: "include",
        },
      );
      if (!response || !response.ok) return;
      const data = await response.json();
      setClassTests(data);
    } catch (err) {
      swalError("Failed to fetch class tests!");
    } finally {
      setIsLoadingTests(false);
    }
  };

  const handleEditClick = (cls) => {
    const formattedEnrollmentEndDate = cls.enrollmentEndDate
      ? userDate(cls.enrollmentEndDate)
      : "";
    const currentLevel =
      cls.level ||
      (courses.length > 0 && cls.courseId
        ? courses.find((c) => c.id === cls.courseId || c._id === cls.courseId)
            ?.level
        : null);
    setEditClass({
      ...cls,
      enrollmentEndDate: formattedEnrollmentEndDate,
      name: cls.name || "",
      level: currentLevel,
      instructorEditingAllowed: cls.instructorEditingAllowed ?? true,
      instructorDeletionAllowed: cls.instructorDeletionAllowed ?? true,
    });
    const hasIndirect =
      cls.indirectCOAttainments && cls.indirectCOAttainments.length > 0;
    setIsIndirectAttainment(hasIndirect);
    setInitialEditClass({
      instructorId: cls.instructorId,
      courseId: cls.courseId,
      enrollmentEndDate: formattedEnrollmentEndDate,

      name: cls.name || "",

      level: currentLevel,
      periodId: cls.periodId,
      instructorEditingAllowed: cls.instructorEditingAllowed ?? true,
      instructorDeletionAllowed: cls.instructorDeletionAllowed ?? true,
      indirectCOAttainments: cls.indirectCOAttainments || [],
      isIndirectAttainment: hasIndirect,
    });
    setIsEditModalOpen(true);
    setIsEditChanged(false);
    fetchClassTests(cls._id || cls.id); // Fetch tests for the class
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditClass((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (editClass && initialEditClass) {
      const fieldsToCheck = [
        "instructorId",
        "courseId",
        "periodId",
        "enrollmentEndDate",

        "name",

        "level",
        "instructorEditingAllowed",
        "instructorDeletionAllowed",
      ];

      // Check simple fields
      const simpleFieldsChanged = fieldsToCheck.some(
        (key) => editClass[key] !== initialEditClass[key],
      );

      // Check indirectCOAttainments
      const indirectCOChanged = !areIndirectCOAttainmentsEqual(
        editClass.indirectCOAttainments,
        initialEditClass.indirectCOAttainments,
      );
      // Check Indirect CO Attainment toggle state
      const isIndirectToggleChanged =
        isIndirectAttainment !== initialEditClass.isIndirectAttainment;
      setIsEditChanged(
        simpleFieldsChanged || indirectCOChanged || isIndirectToggleChanged,
      );
    } else {
      setIsEditChanged(false);
    }
  }, [editClass, initialEditClass, courses.length, isIndirectAttainment]);

  const handleEditSelectChange = (selectedOption, { name }) => {
    const value = selectedOption ? selectedOption.value : "";
    if (name === "instructorId" && classTests.length > 0) {
      // Prevent changing instructor if tests exist
      return;
    }
    let updatedData = { [name]: value };

    if (name === "instructorId") {
      updatedData.instructorId = value;
    } else if (name === "periodId") {
      const selectedPeriod = periods.find(
        (p) => p.id === selectedOption.value || p._id === selectedOption.value,
      );
      const enrollmentEndDate = selectedPeriod
        ? userDate(selectedPeriod.enrollmentEndDate)
        : "";
      updatedData.enrollmentEndDate = enrollmentEndDate;
    } else if (name === "courseId" && courses.length > 0) {
      const selectedCourse = courses.find(
        (c) => c.id === value || c._id === value,
      );
      updatedData.level = selectedCourse?.level;
    } else if (name === "level") {
      updatedData.level = value;
    }

    setEditClass((prev) => ({
      ...prev,
      ...updatedData,
    }));
  };

  const handleEditClass = async (e) => {
    e.preventDefault();
    if (!editClass.instructorId) {
      swalError("Please select an Instructor.");
      return;
    }
    if (courses.length === 0 && !editClass.level) {
      swalError("Please select a Level.");
      return;
    }

    setIsUpdating(true);
    try {
      const changedFields = Object.keys(initialEditClass).filter((key) => {
        if (key === "isIndirectAttainment") return false;
        if (key === "level" && courses.length > 0) {
          const initialCourse = courses.find(
            (c) =>
              c.id === initialEditClass.courseId ||
              c._id === initialEditClass.courseId,
          );
          const currentCourse = courses.find(
            (c) => c.id === editClass.courseId || c._id === editClass.courseId,
          );
          return initialCourse?.level !== currentCourse?.level;
        }
        return editClass[key] !== initialEditClass[key];
      });

      const classToUpdate = changedFields.reduce((acc, key) => {
        if (key === "enrollmentEndDate" && editClass[key]) {
          acc[key] = convertToUserTimezone(editClass[key]);
        } else if (key === "name") {
          acc.name = editClass[key];
        } else if (key === "level") {
          if (courses.length > 0 && editClass.courseId) {
            const selectedCourse = courses.find(
              (c) =>
                c.id === editClass.courseId || c._id === editClass.courseId,
            );
            acc[key] = selectedCourse?.level;
          } else {
            acc[key] = editClass[key];
          }
        } else {
          acc[key] = editClass[key];
        }
        return acc;
      }, {});

      if (
        courses.length > 0 &&
        changedFields.includes("courseId") &&
        !changedFields.includes("level")
      ) {
        const selectedCourse = courses.find(
          (c) => c.id === editClass.courseId || c._id === editClass.courseId,
        );
        classToUpdate.level = selectedCourse?.level;
      }

      if (courses.length === 0) {
        delete classToUpdate.courseId;
      }

      if (isIndirectAttainment !== initialEditClass.isIndirectAttainment) {
        classToUpdate.indirectCOAttainments = isIndirectAttainment
          ? editClass.indirectCOAttainments || []
          : [];
      }

      const response = await fetchWithAuth(
        `${serverUrl}/api/class/update/${editClass.id || editClass._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(classToUpdate),
          credentials: "include",
        },
      );
      if (!response) return;
      if (!response.ok) {
        let errorText;
        try {
          errorText = await response.json();
        } catch {
          errorText = await response.text();
        }
        throw new Error(
          errorText.message || "Failed to edit class. Please try again.",
        );
      }
      await fetchClasses();
      setEditClass(null);
      setInitialEditClass(null);
      setIsEditModalOpen(false);
      setIsEditChanged(false);
      setClassTests([]); // Reset class tests
      setIsLoadingTests(false); // Reset loading state
      swalSuccess("Class edited successfully!");
    } catch (error) {
      swalError("Failed to edit class!");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteClass = async (classId) => {
    const result = await swalConfirm(
      "Are you sure you want to delete this class?",
      "",
      "Yes, delete it!",
      "No, Cancel",
    );

    if (result.isConfirmed) {
      setDeletingId(classId);
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/class/delete/${classId}`,
          {
            method: "DELETE",
            credentials: "include",
          },
        );
        if (!response) return;
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to delete class.");
        }
        setClasses((prevClasses) =>
          prevClasses.filter((cls) => (cls.id || cls._id) !== classId),
        );
        swalSuccess("Class deleted successfully!");
      } catch (err) {
        swalError(err.message || "Failed to delete class.");
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleEnrollmentsClick = (classId) => {
    setSelectedClassId(classId);
    setIsEnrollmentsModalOpen(true);
  };

  const handleTestsClick = (cls) => {
    setSelectedClassForTests(cls);
    setIsTestsModalOpen(true);
  };

  const instructorOptions = instructors.map((instructor) => ({
    value: instructor._id,
    label: `${instructor.firstName} ${instructor.lastName} (${instructor.email})`,
  }));

  const courseOptions = courses.map((course) => ({
    value: course.id || course._id,
    label: `${course.code.charAt(0).toUpperCase() + course.code.slice(1)} - ${
      course.name
    }`,
  }));

  const periodOptions = periods.map((period) => ({
    value: period.id || period._id,
    label: `${period.term.charAt(0).toUpperCase() + period.term.slice(1)} ${
      period.year
    }`,
  }));

  const formatClassName = (cls) => {
    if (!cls) return "";
    if (cls.isPracticeZone === true || cls.name === "Practice Zone") {
      const student =
        cls.student ||
        (Array.isArray(cls.students) ? cls.students[0] : null) ||
        cls.studentId;
      const studentName =
        student && typeof student === "object"
          ? `${student.firstName || ""} ${student.lastName || ""}`.trim()
          : typeof student === "string"
          ? student.trim()
          : (cls.studentName || "").trim();

      if (studentName) {
        return `${cls.name} (${studentName})`;
      }
    }
    return cls.name || "";
  };

  const enhancedClasses = classes.map((cls) => {
    const instructor = instructors.find((i) => i._id === cls.instructorId);
    const instructorName = instructor
      ? `${instructor.firstName} ${instructor.lastName}`
      : "Unknown";

    const course = courses.find(
      (c) => c.id === cls.courseId || c._id === cls.courseId,
    );
    const courseName = course ? course.name : "-";

    const period = periods.find(
      (p) => p.id === cls.periodId || p._id === cls.periodId,
    );
    const periodName = period ? `${period.term} ${period.year}` : "-";

    return {
      ...cls,
      level: isLanguageInst && cls.level ? (
        <span
          className={`cefr-badge cefr-${String(cls.level)
            .toLowerCase()
            .replace(/\./g, "-")}`}
        >
          {cls.level}
        </span>
      ) : (
        cls.level
      ),
      name: formatClassName(cls),
      instructorName,
      courseName,
      periodName,
      enrollments: (
        <button
          className="edit-btn"
          onClick={() => handleEnrollmentsClick(cls._id || cls.id)}
          disabled={isUpdating || deletingId !== null}
        >
          <i className="fa-solid fa-users"></i>
        </button>
      ),
      tests: (
        <button
          className="edit-btn"
          onClick={() => handleTestsClick(cls)}
          disabled={isUpdating || deletingId !== null}
        >
          <i className="fa-solid fa-clipboard-list"></i>
        </button>
      ),
      edit: (
        <button
          className="edit-btn"
          onClick={() => handleEditClick(cls)}
          disabled={isUpdating || deletingId !== null}
        >
          <i className="fa-solid fa-pen-to-square"></i>
        </button>
      ),
      delete: (
        <button
          className="delete-btn"
          onClick={() => handleDeleteClass(cls._id || cls.id)}
          disabled={isUpdating || deletingId !== null}
        >
          {deletingId === (cls._id || cls.id) ? (
            <i className="fa-solid fa-spinner fa-spin"></i>
          ) : (
            <i className="fa-solid fa-trash-can"></i>
          )}
        </button>
      ),
    };
  });

  const renderClassDetails = (cls) => {
    if (!cls) return null;
    return (
      <div
        className="class-details"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "2rem",
          flexWrap: "wrap",
        }}
      >
        <p style={{ margin: 0 }}>
          <strong>{isLanguageInst ? "CEFR Level:" : "Level:"}</strong> {cls.level}
        </p>
        <p style={{ margin: 0 }}>
          <strong>{isLanguageInst ? "Batch Name / Number:" : "Class Name:"}</strong>{" "}
          {formatClassName(cls)}
        </p>
        {institute && institute.curriculum && (
          <p style={{ margin: 0 }}>
            <strong>Curriculum:</strong> {institute.curriculum}
          </p>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="manage-container">
        <div
          className="header"
          style={{
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
            width: "100%",
          }}
        >
          <button
            className="create-btn"
            onClick={() => setIsCreateModalOpen(true)}
          >
            {isLanguageInst ? "Add Batch +" : "Add Class +"}
          </button>
          {periods.length > 0 && (
            <div style={{ minWidth: "200px" }}>
                <Select
                  value={selectedPeriod}
                  onChange={(selected) => {
                    setSelectedPeriod(selected);
                  }}
                  options={[{ value: 'all', label: 'All' }, ...periodOptions]}
                  placeholder="Select Period"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
            </div>
          )}
        </div>
      </div>
      <div className="manage-container">
        {loading && <Loader />}
        {error && <div className="error-message">{error}</div>}
        {!loading && !error && (
          <Table data={enhancedClasses} columns={columns} caption={isLanguageInst ? "Batches" : "Classes"} />
        )}
      </div>

      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          if (!isCreating) {
            setIsCreateModalOpen(false);
            resetCreateForm();
          }
        }}
      >
        <h2>{isLanguageInst ? "Create New Batch" : "Create New Class"}</h2>
        <form onSubmit={handleCreateClass}>
          <div className="form-group">
            <label htmlFor="instructorId">
              <i className="fa-solid fa-asterisk requiredIcon"></i> Instructor
            </label>
            <Select
              id="instructorId"
              name="instructorId"
              options={instructorOptions}
              value={
                newClass.instructorId
                  ? instructorOptions.find(
                      (option) => option.value === newClass.instructorId,
                    ) || null
                  : null
              }
              onChange={(selectedOption) => {
                setNewClass({
                  ...newClass,
                  instructorId: selectedOption ? selectedOption.value : "",
                });
              }}
              isClearable
              placeholder="Select or search instructor..."
              required
              isDisabled={isCreating}
            />
            <small>You can search for an instructor by name or email.</small>
          </div>
          {courses.length > 0 ? (
            <div className="form-group">
              <label htmlFor="courseId">
                <i className="fa-solid fa-asterisk requiredIcon"></i> Course
              </label>
              <Select
                id="courseId"
                name="courseId"
                options={courseOptions}
                value={
                  newClass.courseId
                    ? courseOptions.find(
                        (option) => option.value === newClass.courseId,
                      ) || null
                    : null
                }
                onChange={(selectedOption) => {
                  const selectedCourse = courses.find(
                    (course) =>
                      course.id === selectedOption?.value ||
                      course._id === selectedOption?.value,
                  );
                  setNewClass({
                    ...newClass,
                    courseId: selectedOption ? selectedOption.value : "",
                    name: selectedCourse ? selectedCourse.name : "",
                  });
                }}
                isClearable
                placeholder="Select or search course..."
                required
                isDisabled={isCreating}
              />
              <small>You can search for a course by name or code.</small>
            </div>
          ) : null}
          <div className="form-group">
            <label htmlFor="className">
              <i className="fa-solid fa-asterisk requiredIcon"></i>{" "}
              {isLanguageInst ? "Batch Name / Number" : "Class Name"}
            </label>
            {institute?.category !== "HIGHER_EDUCATION" && !isLanguageInst ? (
              <Select
                id="className"
                name="name"
                options={subjects}
                value={
                  newClass.name
                    ? subjects.find((opt) => opt.value === newClass.name) ||
                      { value: newClass.name, label: newClass.name }
                    : null
                }
                onChange={(selectedOption) => {
                  setNewClass((prev) => ({
                    ...prev,
                    name: selectedOption ? selectedOption.value : "",
                  }));
                }}
                isClearable
                placeholder="Select class name..."
                required
                isDisabled={isCreating}
              />
            ) : (
              <input
                type="text"
                id="className"
                name="name"
                placeholder={
                  isLanguageInst
                    ? "e.g. Batch 101, German A1 - Morning Batch"
                    : "Enter class name"
                }
                value={newClass.name || ""}
                onChange={handleInputChange}
                required
                disabled={isCreating}
              />
            )}
          </div>

          {courses.length === 0 && (
            <div className="form-group">
              <label htmlFor="level">
                <i className="fa-solid fa-asterisk requiredIcon"></i>{" "}
                {isLanguageInst ? "CEFR Level" : "Level"}
              </label>
              <Select
                id="level"
                name="level"
                options={levels}
                value={
                  newClass.level
                    ? levels.find(
                        (option) => option.value === newClass.level,
                      ) || null
                    : null
                }
                onChange={handleSelectChange}
                placeholder={isLanguageInst ? "Select CEFR Level" : "Select Level"}
                isClearable
                required={true}
                isDisabled={isCreating}
              />
            </div>
          )}

          {periods.length > 0 ? (
            <div className="form-group">
              <label htmlFor="periodId">{isLanguageInst ? "Term / Period" : "Period"}</label>
              <Select
                id="periodId"
                name="periodId"
                options={periodOptions}
                value={
                  newClass.periodId
                    ? periodOptions.find(
                        (option) => option.value === newClass.periodId,
                      ) || null
                    : null
                }
                onChange={handleSelectChange}
                placeholder="Select or search period..."
                isDisabled={isCreating}
              />
              <small>You can search for a period by term or year.</small>
            </div>
          ) : null}
          {isCreating ? (
            <Loader />
          ) : (
            <button type="submit" className="btn-submit">
              {isLanguageInst ? "Create Batch" : "Create Class"}
            </button>
          )}
        </form>
      </Modal>

      {editClass && (
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => {
            if (!isUpdating) {
              setIsEditModalOpen(false);
              setEditClass(null);
              setInitialEditClass(null);
              setIsEditChanged(false);
              setClassTests([]); // Reset class tests
              setIsLoadingTests(false); // Reset loading state
              setIsIndirectAttainment(false); // Reset indirect attainment
            }
          }}
          maxWidth={isIndirectAttainment ? "100%" : undefined}
        >
          <h2>{isLanguageInst ? "Edit Batch" : "Edit Class"}</h2>
          <form onSubmit={handleEditClass}>
            <div className="form-group">
              <label htmlFor="instructorId">
                <i className="fa-solid fa-asterisk requiredIcon"></i>{" "}
                {isLanguageInst ? "Assigned Teacher" : "Instructor"}
              </label>
              <Select
                id="instructorId"
                name="instructorId"
                options={instructorOptions}
                value={
                  editClass.instructorId
                    ? instructorOptions.find(
                        (option) => option.value === editClass.instructorId,
                      ) || null
                    : null
                }
                onChange={(selectedOption) =>
                  handleEditSelectChange(selectedOption, {
                    name: "instructorId",
                  })
                }
                placeholder={isLanguageInst ? "Select or search teacher..." : "Select or search instructor..."}
                isDisabled={
                  isUpdating || isLoadingTests || classTests.length > 0
                }
              />
              {!isLoadingTests && classTests.length === 0 && (
                <small>
                  {isLanguageInst
                    ? "You can search for a teacher by name or email."
                    : "You can search for an instructor by name or email."}
                </small>
              )}
              {isLoadingTests && <small>Loading Instructors...</small>}
              {classTests.length > 0 && (
                <small style={{ color: "red" }}>
                  You can't change the Instructor for this class as there is a
                  Test Created already.
                </small>
              )}
            </div>
            {courses.length > 0 ? (
              <div className="form-group">
                <label htmlFor="courseId">Course</label>
                <Select
                  id="courseId"
                  name="courseId"
                  options={courseOptions}
                  value={
                    editClass.courseId
                      ? courseOptions.find(
                          (option) => option.value === editClass.courseId,
                        ) || null
                      : null
                  }
                  onChange={(selectedOption) => {
                    const selectedCourse = courses.find(
                      (course) =>
                        course.id === selectedOption?.value ||
                        course._id === selectedOption?.value,
                    );
                    handleEditSelectChange(selectedOption, {
                      name: "courseId",
                    });
                    setEditClass((prev) => ({
                      ...prev,
                      name: selectedCourse ? selectedCourse.name : "",
                    }));
                  }}
                  isClearable
                  placeholder="Select or search course..."
                  isDisabled={isUpdating}
                />
                <small>You can search for a course by name or code.</small>
              </div>
            ) : null}
            <div className="form-group">
              <label htmlFor="className">
                <i className="fa-solid fa-asterisk requiredIcon"></i>{" "}
                {isLanguageInst ? "Batch Name / Number" : "Class Name"}
              </label>
              {institute?.category !== "HIGHER_EDUCATION" && !isLanguageInst ? (
                <Select
                  id="className"
                  name="name"
                  options={subjects}
                  value={
                    editClass.name
                      ? subjects.find((opt) => opt.value === editClass.name) ||
                        { value: editClass.name, label: editClass.name }
                      : null
                  }
                  onChange={(selectedOption) => {
                    handleEditSelectChange(selectedOption, { name: "name" });
                  }}
                  isClearable
                  placeholder="Select class name..."
                  isDisabled={isUpdating}
                />
              ) : (
                <input
                  type="text"
                  id="className"
                  name="name"
                  placeholder={
                    isLanguageInst
                      ? "e.g. Batch 101, German A1 - Morning Batch"
                      : "Enter class name"
                  }
                  value={editClass.name || ""}
                  onChange={handleEditInputChange}
                  required
                  disabled={isUpdating}
                />
              )}
            </div>

            {courses.length === 0 && (
              <div className="form-group">
                <label>{isLanguageInst ? "CEFR Level" : "Level"}</label>
                <Select
                  id="level"
                  name="level"
                  options={levels}
                  value={
                    editClass.level
                      ? levels.find(
                          (option) => option.value === editClass.level,
                        ) || null
                      : null
                  }
                  onChange={(selectedOption) =>
                    handleEditSelectChange(selectedOption, { name: "level" })
                  }
                  isClearable
                  placeholder={isLanguageInst ? "Select CEFR level..." : "Select level..."}
                  isDisabled={isUpdating}
                />
              </div>
            )}
            {periods.length > 0 ? (
              <div className="form-group">
                <label htmlFor="periodId">Period</label>
                <Select
                  id="periodId"
                  name="periodId"
                  options={periodOptions}
                  value={
                    editClass.periodId
                      ? periodOptions.find(
                          (option) => option.value === editClass.periodId,
                        ) || null
                      : null
                  }
                  onChange={handleEditSelectChange}
                  placeholder="Select or search period..."
                  isDisabled={isUpdating}
                />
                <small>You can search for a period by term or year.</small>
              </div>
            ) : null}
            {courseHasProgram[editClass.courseId] && (
              <div className="form-group">
                <label>Indirect CO Attainment</label>
                <label className="solution-toggle">
                  <input
                    type="checkbox"
                    name="isIndirectAttainment"
                    checked={isIndirectAttainment}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setIsIndirectAttainment(checked);
                    }}
                    disabled={isUpdating}
                  />
                  <span className="toggle-track">
                    <span className="toggle-indicator"></span>
                  </span>
                  <span className="toggle-label-text">
                    {!isIndirectAttainment
                      ? "Provide Indirect CO Attainment For the class."
                      : ""}
                  </span>
                </label>
              </div>
            )}

            {/* Conditional Indirect CO Attainment Table */}
            {isIndirectAttainment && (
              <div className="table-container">
                {renderIndirectCOAttainmentsTable(true)}
              </div>
            )}

            {isUpdating ? (
              <Loader />
            ) : (
              <button
                type="submit"
                className="btn-submit"
                disabled={!isEditChanged || isUpdating}
                onClick={(e) => {
                  if (!isEditChanged && !isUpdating) {
                    e.preventDefault();
                    swalWarning("No changes detected.");
                  }
                }}
              >
                {isLanguageInst ? "Update Batch" : "Save"}
              </button>
            )}
          </form>
        </Modal>
      )}

      <Modal
        isOpen={isEnrollmentsModalOpen}
        onClose={() => setIsEnrollmentsModalOpen(false)}
        maxWidth="100%"
      >
        {selectedClassId && (
          <>
            {(() => {
              const cls = classes.find(
                (c) => (c.id || c._id) === selectedClassId,
              );
              return cls ? (
                <div style={{ marginBottom: "1rem" }}>
                  {renderClassDetails(cls)}
                </div>
              ) : null;
            })()}
            <ManageEnrollments classId={selectedClassId} />
          </>
        )}
      </Modal>

      <Modal
        isOpen={isTestsModalOpen}
        onClose={() => {
          setIsTestsModalOpen(false);
          setSelectedClassForTests(null);
        }}
        maxWidth="100%"
      >
        {selectedClassForTests && (
          <ManageTests
            classId={selectedClassForTests._id || selectedClassForTests.id}
            classDetails={renderClassDetails(selectedClassForTests)}
          />
        )}
      </Modal>
    </>
  );
});

export default ManageClasses;
